import { useState } from 'react'
import { revokeInvite, type InviteDetail } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'

interface Props {
  invite: InviteDetail
  onClose: () => void
  onUpdate: () => void
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

export function InviteDetailModal({ invite, onClose, onUpdate }: Props) {
  const [revoking, setRevoking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canRevoke = usePermission('invites', 'revoke')

  const handleRevoke = async () => {
    if (!confirm('Are you sure you want to revoke this invite? This cannot be undone.')) return
    setRevoking(true)
    setError(null)
    try {
      const response = await revokeInvite(invite.id)
      if (response.ok) {
        onUpdate()
        onClose()
      } else {
        setError(response.error?.message || 'Failed to revoke invite')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setRevoking(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">Invite Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Status</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[invite.status]}`}>
              {invite.status}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">ID</span>
            <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{invite.id}</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Email</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">{invite.email || '-'}</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Type</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">{invite.invite_type}</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Created</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(invite.created_at)}</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Expires</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(invite.expires_at)}</span>
          </div>

          {invite.accepted_at && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Accepted</span>
              <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(invite.accepted_at)}</span>
            </div>
          )}

          {invite.accepted_by && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Accepted By</span>
              <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{invite.accepted_by}</span>
            </div>
          )}

          {invite.revoked_at && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-28">Revoked</span>
              <span className="text-sm text-gray-900 dark:text-gray-100">{formatDate(invite.revoked_at)}</span>
            </div>
          )}

          {invite.metadata && Object.keys(invite.metadata).length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Metadata</span>
              <pre className="mt-1 rounded-xl bg-gray-50 dark:bg-[#0f0d12] p-3 text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                {JSON.stringify(invite.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-2">
          {canRevoke && invite.status === 'pending' && (
            <CustomButton onClick={handleRevoke} variant="danger" loading={revoking}>
              Revoke Invite
            </CustomButton>
          )}
          <CustomButton onClick={onClose} variant="secondary">
            Close
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
